import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, styled, Typography, useTheme } from "@mui/material";
import WealthAPILogo from "@components/Logo/images/wealthapi-logo.svg?url";
import WealthAPIWhiteLogo from "@components/Logo/images/wealthapi-logo-white.svg?url";
import DiagonalLineIcon from "@icons/myicons/diagonal-line.svg?react";
import { useOidcLogin } from "./hooks/useOidcLogin";

type Props = {
  onLogin: (accessToken: string) => Promise<void> | void;
};

const Card = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(4, 5),
  borderRadius: "12px",
  border: `1px solid ${theme.palette.border.secondary}`,
  width: "100%",
  maxWidth: "800px",
  boxShadow: "0px 2px 4px -1px rgba(21, 40, 75, 0.16)",
}));

export const LoginSSOContent = ({ onLogin }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { login, loading, errorKey } = useOidcLogin({
    onTokenReceived: onLogin,
  });

  return (
    <Card>
      <Box textAlign="center" width="100%">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={4}
          mb={6}
        >
          <Box
            component="img"
            src={
              theme.palette.mode === "dark" ? WealthAPIWhiteLogo : WealthAPILogo
            }
            alt="wealth API"
            sx={{ height: 48 }}
          />
          <DiagonalLineIcon />
        </Box>

        <Typography variant="h4" mb={4} fontWeight={700}>
          {t("loginSSOPage.title")}
        </Typography>
        <Typography variant="body4" color="text.secondary" mb={22}>
          {t("loginSSOPage.description")}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={login}
          disabled={loading}
          sx={{ mb: 17, maxWidth: "630px" }}
        >
          {t("loginSSOPage.buttonLabel")}
        </Button>

        {errorKey && (
          <Typography variant="body4" color="error" mt={4}>
            {t(errorKey)}
          </Typography>
        )}

        <Typography variant="body4" color="text.secondary">
          {t("loginSSOPage.footer", { year: new Date().getFullYear() })}
        </Typography>
      </Box>
    </Card>
  );
};
